import React, {
  createContext,
  useContext,
  useMemo,
  createRef,
  useReducer,
  useCallback,
  useEffect,
} from 'react';
import { INITIAL_STATE, reducer } from './reducer';
import { IAnswer, IEstado, IQuestion, IStateAppProvider } from '../../interfaces';
import { SET_ALERT, SET_CIDADES, SET_CONFIG, SET_DISABLED_BUTTONS, SET_ESTADOS, SET_IS_VISIBLE_PARY_CONFETTI, SET_LOADING, SET_QUESTIONS, SET_STEP, SET_VOUCHER, SET_BAIRROS } from './types';
import { Alert, Loading } from '../../Component';
import { getCidades, getEstados, getListLocation, getSetUp, getVoucher, sendAnswers } from '../../Service/api';
import { useTheme } from '../ThemeProvider';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { locationQuery, onlyNumber, validateCpf } from '../../utils';
import Lottie from "lottie-react";
import  * as animationData from '../../assets/lottie/happy.json';


interface IContext extends IStateAppProvider {
  onLoading(loadingText: string): void;
  onAlert(alertValues: IStateAppProvider['alertValues']): void;
  onSetStep(activeStep: number): void;
  onSetEstados(estados: IStateAppProvider['estados']): void;
  onSetCidades(cidades: IStateAppProvider['cidades']): void;
  onGetCidades(estadoId: IEstado["id"]): void;
  onSetCurrentQuestions(newQuestion: IQuestion, isRemove?: boolean): void;
  onSubmitRegister(values: FieldValues, methods: UseFormReturn<FieldValues, any, undefined>): void;
  onSetDisabledButtons(disabledButtons: boolean): void;
  onSetVoucher(voucher: string): void;
  onSetVisiblePartyConfetti(isVisiblePartyConfetti: boolean): void;
  onSetConfig(config: IStateAppProvider["config"]): void;
  onGetBairros(uf: string, cidade: string, typedLogradouro: string): void;
}

export const AuthContext = createContext({} as IContext);
export let authContextState = createRef<IContext>().current;
export let currentQuestionsRef = createRef<IQuestion[]>().current;

export default function AuthProvider({
  children,
}: {
  children: React.ReactElement;
}) {
  const {setConfigTheme, setisLoadingStartApp} = useTheme();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const onLoading = useCallback((loading: string) => {
    dispatch({
      type: SET_LOADING,
      payload: {
        loading
      }
    });
  }, [])

  const onAlert = useCallback((alertValues: IStateAppProvider['alertValues']) => {
    dispatch({
      type: SET_ALERT,
      payload: {
        alertValues
      }
    });
  }, [])

  const onSetStep = useCallback((activeStep: number) => {
    dispatch({
      type: SET_STEP,
      payload: {
        activeStep
      }
    });
  }, []);

  const onSetQuestions = useCallback((questions: IQuestion[]) => {
    dispatch({
      type: SET_QUESTIONS,
      payload: {
        questions
      }
    });
  }, []);

  const onSetEstados = useCallback((estados: IStateAppProvider['estados']) => {
    dispatch({
      type: SET_ESTADOS,
      payload: {
        estados
      }
    });
  }, [])

  const onSetCidades = useCallback((cidades: IStateAppProvider['cidades']) => {
    dispatch({
      type: SET_CIDADES,
      payload: {
        cidades
      }
    });
  }, [])

  const onSetBairros = useCallback((bairros: IStateAppProvider["bairros"]) => {
    dispatch({
      type: SET_BAIRROS,
      payload: {
        bairros
      }
    });
  }, [])

  const onSetDisabledButtons = useCallback((disabledButtons: boolean) => {
    dispatch({
      type: SET_DISABLED_BUTTONS,
      payload: {
        disabledButtons
      }
    });
  }, [])

  const onSetVoucher = useCallback((voucher: string) => {
    dispatch({
      type: SET_VOUCHER,
      payload: {
        voucher
      }
    });
  }, [])

  const onSetConfig = useCallback((config: IStateAppProvider["config"]) => {
    dispatch({
      type: SET_CONFIG,
      payload: {
        config
      }
    });
  }, [])

  const onSetVisiblePartyConfetti = useCallback((isVisiblePartyConfetti: boolean) => {
    dispatch({
      type: SET_IS_VISIBLE_PARY_CONFETTI,
      payload: {
        isVisiblePartyConfetti
      }
    });
  }, [])

  const onSetCurrentQuestions = useCallback((newQuestion: IQuestion, isRemove = false) => {
    if (!currentQuestionsRef) {
      currentQuestionsRef = [newQuestion];
      return;
    }
    const currentQuestions = currentQuestionsRef?.filter(({index}) => {
      return index !== newQuestion?.index
    })
    !isRemove && currentQuestions.push(newQuestion);
    currentQuestionsRef = currentQuestions;
  }, [])

  const onGetCidades = useCallback(async (estadoId: IEstado["id"]) => {
    onSetCidades({list: [], loading: true})
    try {
      const listCidades = await getCidades(estadoId);
      onSetCidades({list: listCidades, loading: false})
    } catch (error) {
      onSetCidades({list: [], loading: false})
    }
  }, [onSetCidades])

  const onGetBairros = useCallback(async (uf: string, cidade: string, typedLogradouro: string) => {
    if (!uf || !cidade || !typedLogradouro) {
      return;
    }
    onSetBairros({list: [], loading: true})
    try {
      const listBairros = await getListLocation(uf, cidade, typedLogradouro);
      onSetBairros({list: listBairros, loading: false})
    } catch (error) {
      onSetBairros({list: [], loading: false})
    }
  }, [onSetBairros])

  const onSubmitRegister = useCallback(async (
    values: FieldValues,
    methods: UseFormReturn<FieldValues, any, undefined>
  ) => {
    const {activeStep, config} = state;
    if (!activeStep || !currentQuestionsRef) return;
    
    let isValidate = true;
    let answersList: IAnswer[] = [];

    currentQuestionsRef?.map((question) => {
      const key = question?.index;
      let answer = {} as IAnswer;

      // validate inputs ----------------------
      if (values[key] === '') {
        isValidate = false;
        methods?.setError(key.toString(), {
          type: "value",
          message: 'Preencha este campo!',
        })
      }

      if (values[key] === null || values[key] === '-1') {
        isValidate = false;
        methods?.setError(key.toString(), {
          type: "value",
          message: 'Selecione uma opção neste campo!',
        })
      }

      if (values[key]?.length === 0) {
        isValidate = false;
        methods?.setError(key.toString(), {
          type: "value",
          message: 'Selecione uma ou mais opção neste campo!',
        })
      }

      if (question?.mask === 'cpf' && !validateCpf(onlyNumber(values[key]))) {
        isValidate = false;
        methods?.setError(key.toString(), {
          type: "pattern",
          message: 'Digite um CPF válido.',
        })
      }

      if (question?.mask === 'date' && values[key].length < 10){
        isValidate = false;
        methods?.setError(key.toString(), {
          type: "pattern",
          message: 'Digite a data correta, ex:. dd/mm/aaaa',
        })
      }

      if ((question?.mask === 'cep' || question?.mask === 'cep-autocomplete') && values[key].length < 9){
        isValidate = false;
        methods?.setError(key.toString(), {
          type: "pattern",
          message: 'Digite o CEP corretamente.',
        })
      }
      // validate inputs ----------------------

      answer.index = question?.index as string;
      answer.partnerId = config?.partnerId;
      answer.question = question?.question as string;
      answer.type = question?.mask || question?.type;
      if (values[key]) answer.answer = values[key];
      if (question?.fieldNameVoucher) answer.fieldNameVoucher = question?.fieldNameVoucher;

      answersList.push(answer);
      return question;
    })

    if (isValidate) {
      onLoading('Gerando cupom...');
      onSetDisabledButtons(true);
      
      try {
        const requestVoucher = {
          email: answersList?.find(v => v.fieldNameVoucher === 'email')?.answer,
          phone: onlyNumber(answersList?.find(v => v.fieldNameVoucher === 'phone')?.answer as string),
          firstName: answersList?.find(v => v.fieldNameVoucher === 'firstName')?.answer,
          lastName: answersList?.find(v => v.fieldNameVoucher === 'lastName')?.answer,
          partnerId: state?.config?.partnerId,
          utmSource: locationQuery().get('utm_source'),
          tid: locationQuery().get('tid')
        }
        const response = await getVoucher(requestVoucher);

        if(response?.error){
          onAlert({message: response?.msg, severity: 'error'});
          onLoading('');
          onSetDisabledButtons(false);
          return false;
        }else{
          answersList = answersList.map(answer => {
            answer.voucher = response?.invitation_code;
            return answer;
          })
          await sendAnswers({
            firstName: answersList?.find(v => v.fieldNameVoucher === 'firstName')?.answer as string,
            lastName: answersList?.find(v => v.fieldNameVoucher === 'lastName')?.answer as string,
            phone: onlyNumber(answersList?.find(v => v.fieldNameVoucher === 'phone')?.answer as string),
            email: answersList?.find(v => v.fieldNameVoucher === 'email')?.answer as string,
            cpf: answersList?.find(v => v.type === 'cpf')?.answer as string,
            rg: answersList?.find(v => v.type === 'rg' || v.type === 'rgRne')?.answer as string,
            birthDate: answersList?.find(v => v.fieldNameVoucher === 'birthDate')?.answer as string,
            list: answersList,
          });
          onSetVoucher(response?.invitation_code);
          onSetStep(activeStep + 1);
          onSetVisiblePartyConfetti(true);
          onSetDisabledButtons(false);
        }
      } catch (error: any) {
        onAlert({
          message: error?.response?.data?.msg || "Ocorreu um erro ao tentar gerar o cupom.",
          severity: 'error'
        });
      }
      onLoading('');
    }
  }, [
    onAlert,
    onLoading,
    onSetDisabledButtons,
    onSetStep,
    onSetVisiblePartyConfetti,
    onSetVoucher,
    state
  ])

  const onLoadApp = useCallback(async () => {
    setisLoadingStartApp(true);
    try {
      const setup = await getSetUp();
      setConfigTheme(setup?.config?.theme);
      onSetConfig({
        partnerId: setup?.config?.partnerId,
        assets: setup?.config?.assets,
        steps: setup?.config?.steps,
      });
      onSetQuestions(setup?.questions);

      onSetEstados({list: [], loading: true})
      const listEstados = await getEstados();
      onSetEstados({list: listEstados, loading: false})
      setisLoadingStartApp(false);
    } catch (error) {
      onSetEstados({list: [], loading: false})
      setisLoadingStartApp(true);
      onAlert({message: 'Ocorreu um erro ao tentar obter as configurações', severity: 'error', autoHideDuration: 0});
    }
    
  }, [onAlert, onSetConfig, onSetEstados, onSetQuestions, setConfigTheme, setisLoadingStartApp]);

  useEffect(() => {
    onLoadApp();
  }, [onLoadApp]);

  const value = useMemo(
    () => ({
      ...state,
      onLoading,
      onAlert,
      onSetStep,
      onSetEstados,
      onSetCidades,
      onGetCidades,
      onSetCurrentQuestions,
      onSubmitRegister,
      onSetDisabledButtons,
      onSetVoucher,
      onSetVisiblePartyConfetti,
      onSetConfig,
      onGetBairros
    }),
    [
      state,
      onLoading,
      onAlert,
      onSetStep,
      onSetEstados,
      onSetCidades,
      onGetCidades,
      onSetCurrentQuestions,
      onSubmitRegister,
      onSetDisabledButtons,
      onSetVoucher,
      onSetVisiblePartyConfetti,
      onSetConfig,
      onGetBairros
    ],
  );

  authContextState = value;

  return (
    <AuthContext.Provider value={value}>
      {children}
      <Loading />
      <Alert />
      {state?.isVisiblePartyConfetti && (
        <Lottie
          style={{
            position: 'fixed',
            top: 0,
            width:'100vw',
            height: '100vh',
            zIndex: 1
          }}
          animationData={animationData}
          loop={false}
          autoPlay={true}
          onComplete={() => onSetVisiblePartyConfetti(false)}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useApp() {
  const context: IContext = useContext(AuthContext);
  return context;
}
